import React, { useEffect, useState } from 'react';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { Button } from '@uhg-abyss/web/ui/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { DragAndDrop } from '@uhg-abyss/web/ui/DragAndDrop';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { event } from '@uhg-abyss/web/tools/event';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { useGetToolPreferencesQuery } from '../../../../common/hooks/useToolPreferencesQuery';
import { useSaveToolPreferencesQuery } from '../../../../common/hooks/useSaveToolPreferences';
import { validatePermissions } from '../../../../redux/resources/user/helpers';
import { getFeature } from '../../../../redux/tools/features';

let timeout = null;

const WidgetContainer = styled('div', {
  width: '100%',
});
const DrawerHeader = styled('div', {
  display: 'flex',
  marginLeft: 0,
  padding: '16px 20px 10px',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
});
const DrawerTitle = styled('div', {
  fontFamily: 'UHCSerif,Georgia Bold,serif',
  fontSize: '26px',
  fontWeight: 600,
  lineHeight: '48px',
  color: '$primary1',
  width: '60%',
});
const DrawerInfo = styled('div', {
  padding: '10px 20px',
  width: '100%',
});
const DrawerInfoText = styled('div', {
  lineHeight: '20px',
  width: '85%',
});
const CustomizeHomePageButton = styled(Button, {
  '&:hover': {
    backgroundColor: '#D9E9FA !important',
  },
});
const UpdatesContent = styled('div', {
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
});
const UpdatesText = styled('span', {
  marginRight: '100px',
});
const UpdatesSubText = styled('span', {
  fontStyle: 'italic',
  fontSize: '14px',
  color: '$gray6',
  marginRight: '10px',
});
export const CustomizeHomePageDrawerWidget = () => {
  const { data } = usePropsContext();
  const {
    isFlyoutOpen,
    setIsFlyoutOpen,
    user,
    setWidgetTabsOrder,
    setIsTabPreferencesLoading,
  } = data;
  const { isTechnicalUser } = user;
  const isDragUserDisabled = !!isTechnicalUser;
  const { corporateProviderMpin, taxIdNumber, servicingProviderMpin } =
    user.provider;
  const appPermissions = {
    claims: validatePermissions(user, 'claims.read'),
    eligibility: validatePermissions(user, 'eligibility.read'),
    referrals: validatePermissions(user, 'referrals.read'),
    paan: validatePermissions(user, 'prior-auth.read'),
  };
  const allTabs = [
    'actionRequired',
    'eligibility',
    'claims',
    'referrals',
    'priorAuth',
    'doclib',
    'uhcUpdates',
  ];
  const [isEligVisible, setIsEligVisible] = useState(true);
  const [isClaimsVisible, setIsClaimsVisible] = useState(true);
  const [isReferralsVisible, setIsReferralsVisible] = useState(true);
  const [isPaanVisible, setIsPaanVisible] = useState(true);
  const [isDocVisible, setIsDocVisible] = useState(true);
  const mainToolsColumn = {
    id: 'main-tools',
    columnContent: 'Main Tools',
    isVisible: true,
    hideAction: true,
  };

  const mainToolKeys = {
    actionRequired: {
      state: true,
      permissions: true,
      props: { isDragDisabled: true },
      content: 'Action Required',
      toggle: 'TABBED_ACTION_REQUIRED_WIDGET',
    },
    eligibility: {
      content: 'Eligibility',
      state: isEligVisible,
      callbackFn: setIsEligVisible,
      permissions: appPermissions.eligibility,
    },
    claims: {
      content: 'Claims & Payments',
      state: isClaimsVisible,
      callbackFn: setIsClaimsVisible,
      permissions: appPermissions.claims,
    },
    priorAuth: {
      content: 'Prior Authorizations & Notifications',
      state: isPaanVisible,
      callbackFn: setIsPaanVisible,
      permissions: appPermissions.paan,
    },
    referrals: {
      content: 'Referrals',
      state: isReferralsVisible,
      callbackFn: setIsReferralsVisible,
      permissions: appPermissions.referrals,
    },
    doclib: {
      content: 'Documents & Reporting',
      state: isDocVisible,
      callbackFn: setIsDocVisible,
      permissions: true,
    },
    uhcUpdates: {
      content: () => {
        return (
          <UpdatesContent>
            <UpdatesText>UnitedHealthcare Updates</UpdatesText>
            <UpdatesSubText>This is always visible</UpdatesSubText>
          </UpdatesContent>
        );
      },
      state: true,
      permissions: true,
      props: { isDragDisabled: isDragUserDisabled },
    },
  };

  const handleVisibilityProps = (stateVariable, callbackFn, content) => {
    return {
      isVisible: stateVariable,
      action: {
        icon: (
          <IconMaterial
            icon={stateVariable ? 'visibility' : 'visibility_off'}
            color={stateVariable ? '$interactive1' : '$gray6'}
            variant="filled"
            size="18px"
          />
        ),
        method: (row) => {
          if (!isTechnicalUser) {
            event('CUSTOMIZE_HOMEPAGE_MAIN_TOOL_SELECT_DESELECT', {
              toolName: content,
              toolSelected: !row.isVisible,
            });
            callbackFn(!row.isVisible);
          }
        },
      },
    };
  };

  const [dndData, setDndData] = useState([]);

  const mapMainToolsOrder = (responseContent) => {
    // map over responseContent items to return column item values in order of responseContent
    const newOrderMap = responseContent
      .filter((item) => {
        const hasPermissions = mainToolKeys[item].permissions;
        const activeToggle = mainToolKeys[item].toggle
          ? getFeature(mainToolKeys[item].toggle)
          : true;

        return hasPermissions && activeToggle;
      })
      .map((item) => {
        const isUneditableContent =
          item === 'uhcUpdates' || item === 'actionRequired';
        const mainToolKey = mainToolKeys[item];
        const columnItem = {
          id: item,
          props: {
            isDragDisabled:
              mainToolKey?.props?.isDragDisabled || isDragUserDisabled,
          },
          content: mainToolKey.content,
        };
        if (!isUneditableContent) {
          mainToolKey.callbackFn(true);
        }
        if (isUneditableContent) {
          columnItem.isVisible = true;
          columnItem.action = {
            icon: <IconMaterial icon="lock" color="$gray6" size="18px" />,
            method: () => {},
          };
        }

        return {
          ...columnItem,
          ...(!isUneditableContent &&
            mainToolKey &&
            handleVisibilityProps(
              true,
              mainToolKey.callbackFn,
              mainToolKey.content
            )),
        };
      });

    // Tabs NOT in responseContent
    const mappedInvisibleContent = allTabs
      .filter((tab) => mainToolKeys[tab].permissions)
      .map((tab) => {
        const isUneditableContent =
          tab === 'uhcUpdates' || tab === 'actionRequired';
        const mainToolKey = mainToolKeys[tab];
        if (!isUneditableContent && !responseContent.includes(tab)) {
          mainToolKey.callbackFn(false);
        }

        const columnItem = {
          id: tab,
          content: mainToolKey.content,
          props: {
            isDragDisabled:
              mainToolKey?.props?.isDragDisabled || isDragUserDisabled,
          },
        };

        return {
          ...columnItem,
          ...(!isUneditableContent &&
            mainToolKey &&
            handleVisibilityProps(
              false,
              mainToolKey.callbackFn,
              mainToolKey.content
            )),
        };
      })
      .filter(
        (tab) => !responseContent.find((responseTab) => responseTab === tab.id)
      );
    mappedInvisibleContent.forEach((item) => {
      newOrderMap.push(item);
    });

    return newOrderMap;
  };

  const [isOrderSaved, setIsOrderSaved] = useState(false);
  const [initialOrder, setInitialOrder] = useState([]); // initial order only used to check if user rearranged tab order during customization
  const [toolPreferencesResponse, getToolPreferences] =
    useGetToolPreferencesQuery({
      onCompleted: (response) => {
        const orderPrefs =
          response.orderPreferences && response.orderPreferences.length > 0
            ? ['actionRequired', ...response.orderPreferences]
            : allTabs;

        const mainTools = [
          {
            ...mainToolsColumn,
            columnItems: mapMainToolsOrder(orderPrefs),
          },
        ];
        setDndData(mainTools); // update row items in flyout
        setWidgetTabsOrder(orderPrefs); // update visibililty/arrangement of tabs
        setInitialOrder(orderPrefs); // update initial order to order passed in. this initial order will be used next time user opens the flyout (if no PUT requests are made to update order)
        setIsTabPreferencesLoading(false);
      },
      onError: (err) => {
        const mainTools = [
          {
            ...mainToolsColumn,
            columnItems: mapMainToolsOrder(allTabs),
          },
        ];
        setDndData(mainTools); // update row items in flyout
        setWidgetTabsOrder(allTabs); // update visibililty/arrangement of tabs
        setInitialOrder(allTabs); // update initial order to order passed in. this initial order will be used next time user opens the flyout (if no PUT requests are made to update order)
        setIsTabPreferencesLoading(false);
      },
    });
  const [, saveToolPreferences] = useSaveToolPreferencesQuery({});

  const mainToolsOrder =
    dndData.length > 0 &&
    dndData[0].columnItems
      .filter((tool) => tool.isVisible)
      .map((tool) => tool.id);
  const isOrderChanged =
    JSON.stringify(initialOrder) !== JSON.stringify(mainToolsOrder) || false;

  useEffect(() => {
    if (
      (user.uuid && corporateProviderMpin, taxIdNumber, servicingProviderMpin)
    ) {
      setDndData([]); // update row items in flyout
      getToolPreferences({
        variables: {
          uuid: user.uuid,
          mpinTinCareProvider: `${corporateProviderMpin}_${taxIdNumber}_${servicingProviderMpin}`,
        },
      });
    }
  }, [user.uuid, corporateProviderMpin, taxIdNumber, servicingProviderMpin]);

  const handleNameFiltering = (items) => {
    const orderArray = [];

    items.forEach((item) => {
      if (item.isVisible) {
        orderArray.push(item.id);
      }
    });

    return orderArray;
  };

  const handleVisibilityChange = () => {
    if (dndData.length > 0) {
      const { columnItems } = dndData[0];
      const remapVisibility = columnItems.map((rowData) => {
        const isUneditableContent =
          rowData.id === 'uhcUpdates' || rowData.id === 'actionRequired';
        const newRow = rowData;
        const mainToolKey = mainToolKeys[newRow.id];

        return {
          ...newRow,
          ...(!isUneditableContent &&
            mainToolKey &&
            handleVisibilityProps(
              mainToolKey.state,
              mainToolKey.callbackFn,
              mainToolKey.content
            )),
        };
      });

      const newOrderArray = handleNameFiltering(remapVisibility); // returns array of names (strings) in order, only returns items that are visible
      const newWithoutActionRequired = newOrderArray.filter(
        (id) => id !== 'actionRequired'
      ); // remove actionRequired since trackit tool preferences api doesnt support it

      setIsOrderSaved(true);

      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        saveToolPreferences({
          variables: {
            skipCustomization: false,
            uuid: user.uuid,
            mpinTinCareProvider: `${corporateProviderMpin}_${taxIdNumber}_${servicingProviderMpin}`,
            orderPreferences: newWithoutActionRequired,
          },
        });

        timeout = null;
      }, 2000);

      const newContentObject = {
        ...mainToolsColumn,
        columnItems: remapVisibility,
      };
      setDndData([newContentObject]); // update row items in flyout
      setWidgetTabsOrder(newOrderArray); // update visibililty/arrangement of tabs
      setIsOrderSaved(true);
    }
  };

  useEffect(() => {
    handleVisibilityChange();
  }, [
    isEligVisible,
    isClaimsVisible,
    isReferralsVisible,
    isPaanVisible,
    isDocVisible,
  ]);

  const handleCustomizeHomePageOnClick = () => {
    event('CUSTOMIZE_HOMEPAGE_START_CLICK');
    setIsFlyoutOpen(true);
  };

  const triggerAdobeSaveAndCloseEvent = () => {
    const mainToolsData = dndData[0].columnItems;
    const selectedMainTools = [];
    const deselectedMainTools = [];

    mainToolsData.forEach((tool) => {
      const { content, isVisible } = tool;
      let toolContent = content;
      if (typeof content === 'object') {
        toolContent = content.props.children[0].props.children;
      }
      if (isVisible) {
        selectedMainTools.push(toolContent);
      } else {
        deselectedMainTools.push(toolContent);
      }
    });

    const cusotomizeHomePageSaveData = {
      selectedMainTools,
      deselectedMainTools,
      providerUuid: user.uuid,
      providerTaxId: taxIdNumber,
      providerMpin: corporateProviderMpin,
      mainToolsDefaultOrderChanged: isOrderSaved ? isOrderChanged : false,
    };
    event(
      'CUSTOMIZE_HOMEPAGE_SAVE_AND_CLOSE_CLICK',
      cusotomizeHomePageSaveData
    );
  };

  const handleCancel = () => {
    // if user clicks 'cancel' in customize order, set order of main tool items in flyout back to initial order
    const mainTools = [
      {
        ...mainToolsColumn,
        columnItems: mapMainToolsOrder(initialOrder),
      },
    ];

    setDndData(mainTools); // update row items in flyout
    setWidgetTabsOrder(initialOrder); // update visibililty/arrangement of tabs
    setIsOrderSaved(false); // reset saved flag
  };

  const handleClose = () => {
    if (!isOrderSaved) {
      // if user closes the flyout without saving potential reorders, set order of main tool items in flyout back to initial order
      const mainTools = [
        {
          ...mainToolsColumn,
          columnItems: mapMainToolsOrder(initialOrder),
        },
      ];
      setIsOrderSaved(false);
      setDndData(mainTools); // update row items in flyout
      setWidgetTabsOrder(initialOrder); // update visibililty/arrangement of tabs
    }
    setIsFlyoutOpen(false);
    triggerAdobeSaveAndCloseEvent();
    event('CUSTOMIZE_HOMEPAGE_COMPLETE');
    if (isOrderSaved) {
      setInitialOrder(mainToolsOrder);
      setIsOrderSaved(false);
    }
  };

  const handleReorderSave = () => {
    const { columnItems } = dndData[0];
    const reorderedArray = handleNameFiltering(columnItems); // returns names in order, only returns items that are visible
    const newWithoutActionRequired = reorderedArray.filter(
      (id) => id !== 'actionRequired'
    ); // remove actionRequired since trackit tool preferences api doesnt support it

    setWidgetTabsOrder(reorderedArray); // update visibililty/arrangement of tabs
    setIsOrderSaved(true);
    // save to API
    saveToolPreferences({
      variables: {
        skipCustomization: false,
        uuid: user.uuid,
        mpinTinCareProvider: `${corporateProviderMpin}_${taxIdNumber}_${servicingProviderMpin}`,
        orderPreferences: newWithoutActionRequired,
      },
    });
  };

  return (
    <WidgetContainer>
      <div
        data-testid="drag-and-drop-wrapper"
        style={{
          zIndex: -1,
          position: 'fixed',
          top: '220px',
          right: '0px',
          width: '570px',
          height: '400px',
        }}
      />
      <div data-testid="customize-button-wrapper">
        <CustomizeHomePageButton
          variant="outline"
          size="$sm"
          onClick={handleCustomizeHomePageOnClick}
        >
          Customize Tabs
        </CustomizeHomePageButton>
      </div>
      <Drawer
        isOpen={isFlyoutOpen}
        onClose={handleClose}
        size="$md"
        css={{
          'abyss-modal-close-button': {
            display: 'none',
          },
          'abyss-modal-overlay': {
            zIndex: 1500,
          },
        }}
      >
        <DrawerHeader>
          <DrawerTitle data-testid="customize-tabs-title">
            Customize Tabs
          </DrawerTitle>
          <div>
            <Button
              data-testid="customize-tabs-close-button"
              size="$lg"
              variant="outline"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        </DrawerHeader>
        <DrawerInfo>
          <DrawerInfoText data-testid="customize-tabs-subheading">
            <Text size="$sm">
              You can change the tabs visible within the Main Tools section on{' '}
              <br />
              the homepage by using the{' '}
              <IconMaterial
                icon="visibility"
                size="14px"
                style={{ verticalAlign: 'middle' }}
              />{' '}
              icon to hide or show tabs. You are <br /> also able to click
              "Customize Order" to change the tab order.
              <br />
              <IconMaterial
                icon="visibility"
                size="14px"
                style={{ verticalAlign: 'middle' }}
              />{' '}
              Changes to the page visibility are saved in real time.
            </Text>
          </DrawerInfoText>
        </DrawerInfo>
        <LoadingOverlay
          isLoading={toolPreferencesResponse.loading === true}
          loadingMessage="Retrieving Tool Preferences"
          isOpen={toolPreferencesResponse.loading === true}
          css={{
            'abyss-loading-overlay-root': {
              overflow: 'hidden',
            },
          }}
        >
          <If condition={dndData.length > 0}>
            <div data-testid="drag-and-drop-wrapper" />
            <DragAndDrop
              data={dndData}
              setData={setDndData}
              onSave={handleReorderSave}
              onCancel={handleCancel}
              accordionDisabled
            />
          </If>
        </LoadingOverlay>
      </Drawer>
    </WidgetContainer>
  );
};
